import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import { Box, ButtonBase } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  cacheTagSuggestions,
  getCachedTagSuggestions,
  refreshTagSuggestions,
} from '../services/patientFormCache'
import { usePatientFormI18n } from '../utils/patientFormI18n'
import {
  PATIENT_FORM_TOKENS as T,
  PatientFieldHint,
  PatientFormCell,
  PatientRowDeleteButton,
  PatientSectionTitle,
  PatientTextInput,
} from './PatientFormPrimitives'

const TAG_PALETTE = ['#6779e3', '#E57373', '#FFB74D', '#81C784', '#BA68C8', '#4DB6AC', '#F06292', '#FDD835']

export function pickTagColor(label = '') {
  let hash = 0
  for (let i = 0; i < label.length; i += 1) hash = (hash * 31 + label.charCodeAt(i)) >>> 0
  return TAG_PALETTE[hash % TAG_PALETTE.length]
}

function tagKey(tag) {
  return (tag?.tagCode || tag?.label || '').toString().trim().toLowerCase()
}

function TagChip({ tag, onRemove, disabled }) {
  const color = tag?.color || pickTagColor(tag?.label)
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        maxWidth: '100%',
        paddingLeft: '10px',
        paddingRight: onRemove ? '2px' : '10px',
        height: { xs: '32px', sm: '28px' },
        borderRadius: '999px',
        fontSize: '12.5px',
        fontWeight: 600,
        color: '#1e293b',
        background: `${color}26`,
        border: `1px solid ${color}80`,
      }}
    >
      <Box component="span" sx={{ width: '8px', height: '8px', borderRadius: '50%', background: color, marginRight: '6px', flexShrink: 0 }} />
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {tag?.label}
      </Box>
      {onRemove && !disabled && (
        <PatientRowDeleteButton
          onClick={onRemove}
          label="Remove"
          sx={{ width: '24px', height: '24px', minWidth: 0, '& svg': { fontSize: '15px' } }}
        />
      )}
    </Box>
  )
}

/**
 * Patient tags, always visible: existing tags as chips, an input to add more, and the
 * brand's tag library as one-tap suggestions. New tags ride along in the create / update
 * payload (`tags`), so this works before the patient has an id.
 */
export default function PatientTagsField({
  tags = [],
  editMode = true,
  onAdd,
  onRemove,
  patientTagsApiSignature,
  clinicBrandId,
  // Small control rendered to the right of the tag input (the preferred-language select).
  trailing = null,
  trailingLabel = '',
}) {
  const { copy } = usePatientFormI18n()
  const [draft, setDraft] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [focused, setFocused] = useState(false)
  const loadedRef = useRef(false)

  // Cache first (instant, offline-safe), then a background refresh from the brand library.
  const loadSuggestions = useCallback(async () => {
    if (loadedRef.current) return
    loadedRef.current = true
    const cached = await getCachedTagSuggestions(clinicBrandId)
    if (cached.length > 0) setSuggestions(cached)
    if (!patientTagsApiSignature) return
    const fresh = await refreshTagSuggestions(patientTagsApiSignature, clinicBrandId)
    if (fresh.length > 0) {
      setSuggestions((prev) => {
        const seen = new Set(fresh.map(tagKey))
        return [...fresh, ...prev.filter((tag) => !seen.has(tagKey(tag)))]
      })
    }
  }, [patientTagsApiSignature, clinicBrandId])

  useEffect(() => {
    if (editMode) loadSuggestions()
  }, [editMode, loadSuggestions])

  const existingKeys = useMemo(() => new Set((tags || []).map(tagKey)), [tags])
  const visibleSuggestions = useMemo(() => {
    const needle = draft.trim().toLowerCase()
    return suggestions
      .filter((tag) => tag?.label && !existingKeys.has(tagKey(tag)))
      .filter((tag) => !needle || tag.label.toLowerCase().includes(needle))
      .slice(0, 10)
  }, [suggestions, existingKeys, draft])

  const commitDraft = () => {
    const label = draft.trim()
    if (!label) return
    const match = suggestions.find((tag) => tag?.label?.toLowerCase() === label.toLowerCase())
    const tag = match ? { ...match } : { label, color: pickTagColor(label) }
    onAdd(tag)
    cacheTagSuggestions([tag], clinicBrandId)
    setDraft('')
  }

  const list = Array.isArray(tags) ? tags : []

  return (
    <PatientFormCell span={12}>
      <PatientSectionTitle
        action={
          trailing && trailingLabel ? (
            <Box component="span" sx={{ fontSize: '11px', fontWeight: 600, color: T.muted, letterSpacing: '0.02em' }}>
              {trailingLabel}
            </Box>
          ) : null
        }
      >
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <LocalOfferIcon sx={{ fontSize: '14px' }} />
          {copy.tagsLabel}
        </Box>
      </PatientSectionTitle>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
        {list.map((tag, index) => (
          <TagChip
            key={`${tagKey(tag)}-${index}`}
            tag={tag}
            disabled={!editMode}
            onRemove={editMode ? () => onRemove(index) : undefined}
          />
        ))}
        {list.length === 0 && !editMode && (
          <PatientFieldHint sx={{ marginTop: 0 }}>{copy.tagsEmpty}</PatientFieldHint>
        )}
      </Box>

      {!editMode && trailing && (
        <Box sx={{ marginTop: '8px', maxWidth: { xs: '100%', sm: '220px' } }}>{trailing}</Box>
      )}

      {editMode && (
        <Box sx={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <Box sx={{ position: 'relative', flex: '1 1 auto', minWidth: 0 }}>
          <PatientTextInput
            id="patient-tag-input"
            value={draft}
            placeholder={copy.tagsPlaceholder}
            autoComplete="off"
            onChange={(event) => setDraft(event.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                commitDraft()
              }
            }}
            sx={{ paddingRight: draft ? '76px' : undefined }}
          />
          {draft.trim() && (
            <ButtonBase
              onMouseDown={(event) => event.preventDefault()}
              onClick={commitDraft}
              sx={{
                position: 'absolute',
                right: '6px',
                top: '50%',
                transform: 'translateY(-50%)',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 700,
                color: '#fff',
                background: T.accent,
              }}
            >
              {copy.tagsAddButton}
            </ButtonBase>
          )}
          {(focused || draft) && visibleSuggestions.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
              {visibleSuggestions.map((tag) => (
                <ButtonBase
                  key={tagKey(tag)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onAdd({ ...tag })
                    setDraft('')
                  }}
                  sx={{
                    borderRadius: '999px',
                    padding: '5px 10px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#334155',
                    background: T.surfaceSoft,
                    border: '1px dashed #cbd5e1',
                    '&:hover': { background: '#eef2ff', borderColor: T.accent },
                  }}
                >
                  + {tag.label}
                </ButtonBase>
              ))}
            </Box>
          )}
          {!draft && !focused && list.length === 0 && (
            <PatientFieldHint>{copy.tagsHelper}</PatientFieldHint>
          )}
        </Box>
        {trailing && (
          <Box sx={{ flex: '0 0 auto', width: { xs: '132px', sm: '164px' } }}>{trailing}</Box>
        )}
        </Box>
      )}
    </PatientFormCell>
  )
}
